var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo } from "react";
import { Controller, useFormContext, useWatch, } from "react-hook-form";
import { Box, FormControl, FormGroup, FormControlLabel, Checkbox, FormHelperText, } from "@mui/material";
import { buildFieldValidation } from "../../validators/field-components";
import { getElementValueKey, reorderChoices } from "../../../../utils/helpers";
import CommentArea from "../common/CommentArea";
import { useElementConditions, useFormLogic, } from "../../context/FormLogicContext";
import Header from "../common/Header";
import { getCommentFieldName, getInitialValueForElement, resolveStoreOthersAsComment, resolveTextUpdateMode, } from "../../../../utils/forms-functions";
import OtherOptionInput from "./OtherOptionInput";
import { NONE_VALUE, OTHER_VALUE, SELECT_ALL_VALUE, } from "./constants/special-options";
import { useClearInvisibleValue } from "../../hooks/useClearInvisibleValue";
import { useChoicesConditions } from "../../hooks/useChoicesConditions";
var CheckboxField = function (_a) {
    var element = _a.element, errors = _a.errors;
    var _b = useFormLogic(), globalTextUpdateMode = _b.textUpdateMode, globalStoreOthersAsComment = _b.storeOthersAsComment, globalConstraints = _b.globalConstraints;
    var _c = useFormContext(), unregister = _c.unregister, setValue = _c.setValue, getValues = _c.getValues, trigger = _c.trigger;
    var valueKey = getElementValueKey(element);
    var error = errors[valueKey];
    var _d = useElementConditions(element), isVisible = _d.isVisible, isEnabled = _d.isEnabled, isInteractive = _d.isInteractive, fieldRequired = _d.isRequired;
    var _e = useChoicesConditions(element), choicesVisible = _e.choicesVisible, choicesEnabled = _e.choicesEnabled;
    var validation = useMemo(function () {
        return buildFieldValidation(element, {
            isRequired: fieldRequired,
            readOnly: !isEnabled,
        });
    }, [element, fieldRequired, isEnabled]);
    var textUpdateMode = resolveTextUpdateMode(element, {
        textUpdateMode: globalTextUpdateMode,
    });
    var maxSelectedChoices = element.maxSelectedChoices;
    var storeOthersAsComment = resolveStoreOthersAsComment(element, globalStoreOthersAsComment);
    var commentKey = getCommentFieldName(valueKey);
    var commentValue = useWatch({ name: commentKey });
    var valueKeys = useMemo(function () {
        return element.showCommentArea || storeOthersAsComment
            ? [valueKey, commentKey]
            : [valueKey];
    }, [valueKey, commentKey, element.showCommentArea, storeOthersAsComment]);
    useClearInvisibleValue({
        isVisible: isVisible,
        valueKeys: valueKeys,
        unregister: unregister,
    });
    var choices = useMemo(function () {
        var baseChoices = (element.choices || []);
        var choicesFormatted = baseChoices.map(function (choice) {
            if (typeof choice === "string") {
                return { value: choice, text: choice };
            }
            return choice;
        });
        var orderedChoices = choicesFormatted;
        if (element.choicesOrder) {
            orderedChoices = reorderChoices(orderedChoices, element.choicesOrder);
        }
        var finalChoices = __spreadArray([], orderedChoices, true);
        if (element.showSelectAllItem) {
            finalChoices = __spreadArray([
                {
                    value: SELECT_ALL_VALUE,
                    text: element.selectAllText || "Selecionar todos",
                }
            ], finalChoices, true);
        }
        if (element.showNoneItem) {
            finalChoices = __spreadArray(__spreadArray([], finalChoices, true), [
                { value: NONE_VALUE, text: element.noneText || "Nenhum" },
            ], false);
        }
        return finalChoices;
    }, [element]);
    var baseChoicesValues = useMemo(function () {
        var baseChoices = (element.choices || []);
        return baseChoices.map(function (choice) {
            return typeof choice === "string" ? choice : choice.value;
        });
    }, [element.choices]);
    var selectableChoicesValues = useMemo(function () { return (choicesVisible && choicesEnabled ? baseChoicesValues : []); }, [choicesVisible, choicesEnabled, baseChoicesValues]);
    useEffect(function () {
        if (!choicesVisible) {
            var currentValues = (getValues(valueKey) || []);
            var filteredValues = currentValues.filter(function (value) { return !baseChoicesValues.includes(value); });
            if (filteredValues.length !== currentValues.length) {
                setValue(valueKey, filteredValues);
            }
        }
    }, [choicesVisible, baseChoicesValues, valueKey, getValues, setValue]);
    var isOtherSelectedInValues = function (values) {
        return !!element.showOtherItem &&
            (values.includes(OTHER_VALUE) || !!findOtherTextInValues(values));
    };
    var findOtherTextInValues = function (values) {
        return storeOthersAsComment
            ? undefined
            : values.find(function (value) {
                return !baseChoicesValues.includes(value) &&
                    value !== OTHER_VALUE &&
                    value !== NONE_VALUE &&
                    value !== SELECT_ALL_VALUE;
            });
    };
    var readOtherText = function (values) {
        var _a;
        return storeOthersAsComment
            ? String(commentValue !== null && commentValue !== void 0 ? commentValue : "")
            : (_a = findOtherTextInValues(values)) !== null && _a !== void 0 ? _a : "";
    };
    // A validação roda no mesmo tick do commit, quando o useWatch ainda devolve
    // o valor anterior: aqui é preciso ler o estado vivo do formulário.
    var clearOtherText = function () {
        if (storeOthersAsComment && commentValue) {
            setValue(commentKey, "", { shouldDirty: true });
        }
    };
    var initialValue = getInitialValueForElement(element);
    return (_jsx(_Fragment, { children: isVisible ? (_jsx(Box, __assign({ className: "component-wrapper" }, { children: _jsxs(FormControl, __assign({ component: "fieldset", error: !!error, fullWidth: true, sx: { mb: 3 } }, { children: [_jsx(Header, { element: element, error: !!error, isRequired: fieldRequired }), _jsx(Controller, { name: valueKey, defaultValue: initialValue, rules: {
                            validate: function (value) {
                                var _a;
                                var result = validation.safeParse(value);
                                if (!result.success) {
                                    return ((_a = result.error.errors[0]) === null || _a === void 0 ? void 0 : _a.message) || "Valor inválido";
                                }
                                // Espelha supportGoNextPageError (question_baseselect.ts:577)
                                var values = (value || []);
                                if (isOtherSelectedInValues(values) &&
                                    !readOtherText(values)) {
                                    return (element.otherErrorText ||
                                        "Por favor, insira o outro valor.");
                                }
                                return true;
                            },
                        }, render: function (_a) {
                            var field = _a.field;
                            var currentValues = field.value || [];
                            var isNoneSelected = currentValues.includes(NONE_VALUE);
                            var isSelectAllSelected = currentValues.includes(SELECT_ALL_VALUE);
                            var valuesWithoutNone = currentValues.filter(function (value) { return value !== NONE_VALUE; });
                            var valuesWithoutSelectAll = valuesWithoutNone.filter(function (value) { return value !== SELECT_ALL_VALUE; });
                            var otherValueInArray = findOtherTextInValues(currentValues);
                            var isOtherSelected = isOtherSelectedInValues(currentValues);
                            var committedOtherText = readOtherText(currentValues);
                            var selectedBaseValues = currentValues.filter(function (value) { return selectableChoicesValues.includes(value); });
                            var allBaseSelected = element.showSelectAllItem &&
                                selectedBaseValues.length ===
                                    selectableChoicesValues.length &&
                                selectableChoicesValues.length > 0 &&
                                !isNoneSelected;
                            var hasReachedMaxSelectedChoices = maxSelectedChoices !== undefined &&
                                valuesWithoutSelectAll.length >= maxSelectedChoices;
                            var cannotSelectAll = selectableChoicesValues.length === 0 ||
                                (maxSelectedChoices !== undefined &&
                                    selectableChoicesValues.length > maxSelectedChoices);
                            var handleChoiceChange = function (choiceValue, checked) {
                                if (checked &&
                                    hasReachedMaxSelectedChoices &&
                                    !currentValues.includes(choiceValue)) {
                                    return;
                                }
                                if (choiceValue === NONE_VALUE && checked) {
                                    field.onChange(["none"]);
                                    return;
                                }
                                if (choiceValue === SELECT_ALL_VALUE && checked) {
                                    if (cannotSelectAll) {
                                        return;
                                    }
                                    field.onChange(selectableChoicesValues);
                                    return;
                                }
                                var newValues = checked
                                    ? __spreadArray(__spreadArray([], valuesWithoutSelectAll, true), [choiceValue], false) : valuesWithoutSelectAll.filter(function (value) { return value !== choiceValue; });
                                var newSelectedBaseValues = newValues.filter(function (value) { return selectableChoicesValues.includes(value); });
                                var newAllBaseSelected = element.showSelectAllItem &&
                                    newSelectedBaseValues.length ===
                                        selectableChoicesValues.length &&
                                    selectableChoicesValues.length > 0;
                                var finalValues = newAllBaseSelected
                                    ? __spreadArray(__spreadArray([], newValues, true), [SELECT_ALL_VALUE], false) : newValues;
                                field.onChange(finalValues);
                            };
                            return (_jsxs(FormGroup, { children: [choices
                                        .filter(function (choice) {
                                        return choicesVisible ||
                                            !baseChoicesValues.includes(choice.value);
                                    })
                                        .map(function (choice, index) {
                                        var isChecked = currentValues.includes(choice.value);
                                        if (choice.value === SELECT_ALL_VALUE) {
                                            isChecked = allBaseSelected || isSelectAllSelected;
                                        }
                                        if (isNoneSelected && choice.value !== NONE_VALUE) {
                                            isChecked = false;
                                        }
                                        var isDisabledByMaxSelectedChoices = hasReachedMaxSelectedChoices && !isChecked;
                                        var isSelectAllUnavailable = choice.value === SELECT_ALL_VALUE && cannotSelectAll;
                                        var isDisabledByChoicesEnableIf = baseChoicesValues.includes(choice.value) &&
                                            !choicesEnabled;
                                        return (_jsx(FormControlLabel, { control: _jsx(Checkbox, { checked: isChecked, onChange: function (e) {
                                                    handleChoiceChange(choice.value, e.target.checked);
                                                }, disabled: !isInteractive ||
                                                    isDisabledByMaxSelectedChoices ||
                                                    isSelectAllUnavailable ||
                                                    isDisabledByChoicesEnableIf }), label: choice.text, sx: {
                                                marginBottom: 2,
                                            } }, index));
                                    }), element.showOtherItem && (_jsxs(_Fragment, { children: [_jsx(FormControlLabel, { control: _jsx(Checkbox, { checked: isOtherSelected, onChange: function (e) {
                                                        var newValues = e.target.checked
                                                            ? __spreadArray(__spreadArray([], valuesWithoutSelectAll, true), [OTHER_VALUE], false) : valuesWithoutSelectAll.filter(function (value) {
                                                            return value !== OTHER_VALUE &&
                                                                value !== otherValueInArray;
                                                        });
                                                        if (!e.target.checked) {
                                                            clearOtherText();
                                                        }
                                                        field.onChange(newValues);
                                                    }, disabled: !isInteractive ||
                                                        (hasReachedMaxSelectedChoices &&
                                                            !isOtherSelected) }), label: element.otherText || "Outro" }), isOtherSelected && (_jsx(OtherOptionInput, { committedValue: committedOtherText, textUpdateMode: textUpdateMode, isEnabled: isInteractive, label: element.otherText || "Outro", placeholder: element.otherPlaceholder || "Digite aqui...", maxLength: globalConstraints.maxOthersLength, sx: { mt: 1 }, onCommit: function (text) {
                                                    var valuesWithoutOther = valuesWithoutSelectAll.filter(function (value) {
                                                        return value !== OTHER_VALUE &&
                                                            baseChoicesValues.includes(value);
                                                    });
                                                    if (storeOthersAsComment) {
                                                        setValue(commentKey, text, {
                                                            shouldDirty: true,
                                                        });
                                                        void trigger(valueKey);
                                                        field.onChange(__spreadArray(__spreadArray([], valuesWithoutOther, true), [
                                                            OTHER_VALUE,
                                                        ], false));
                                                        return;
                                                    }
                                                    field.onChange(text
                                                        ? __spreadArray(__spreadArray([], valuesWithoutOther, true), [text], false) : __spreadArray(__spreadArray([], valuesWithoutOther, true), [OTHER_VALUE], false));
                                                } }))] }))] }));
                        } }), error && (_jsx(FormHelperText, { children: error.message })), element.showCommentArea && (_jsx(CommentArea, { element: element, disabled: !isInteractive }))] })) }))) : null }));
};
export default CheckboxField;
